import type { CSSProperties, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import cx from "ui/cx";
import useBrowserLayoutEffect from "ui/hooks/useBrowserLayoutEffect";
import useResizeObserver from "ui/hooks/useResizeObserver";
import styles from "./Split.module.scss";

export enum SplitAlignment {
  NONE = "NONE",
  FIRST = "FIRST",
  LAST = "LAST",
}

interface SplitProps {
  children: [firstChild: ReactNode, lastChild: ReactNode];
  name?: string;
  initialFixedSideSize?: number;
  minPanelSize?: number;
  flexibleSide?: SplitAlignment;
  hideSide?: SplitAlignment;
  draggable?: boolean;
  containerClassNames?: string;
  highlightSeparator?: boolean;
}

interface PanelProps {
  /**
   * When set, scroll behavior will be controlled
   * at instance, rather than managed by default styling
   */
  scrollControlled?: boolean;
}

interface HorizontalSplitProps extends SplitProps {
  leftStyleOverrides?: CSSProperties;
  rightStyleOverrides?: CSSProperties;
  leftProps?: PanelProps;
  rightProps?: PanelProps;
}

interface VerticalSplitProps extends SplitProps {
  bottomStyleOverrides?: CSSProperties;
  topStyleOverrides?: CSSProperties;
}

interface HorizontalSplitMouseInfo {
  event: MouseEvent;
  offsetLeft: number;
  offsetTop: number;
  leftWidth?: number;
  rightWidth?: number;
}

export function HorizontalSplitGroup({
  flexibleSide,
  initialFixedSideSize,
  children: [firstChild, lastChild],
  minPanelSize,
  rightStyleOverrides,
  leftStyleOverrides,
  hideSide = SplitAlignment.NONE,
  draggable = true,
  leftProps,
  rightProps,
  containerClassNames,
  highlightSeparator = false,
}: HorizontalSplitProps) {
  let fixedSide: SplitAlignment;
  if (flexibleSide === SplitAlignment.LAST) {
    fixedSide = SplitAlignment.FIRST;
  } else if (flexibleSide === SplitAlignment.FIRST) {
    fixedSide = SplitAlignment.LAST;
  } else {
    fixedSide = SplitAlignment.NONE;
  }

  const splitGroupRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const separatorRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  const [fixedPanelWidth, setFixedPanelWidth] = useState<number | undefined>(initialFixedSideSize);
  const splitGroupRect = useResizeObserver(splitGroupRef);

  //allow for dynamic updating of panel widths.
  useEffect(() => {
    setFixedPanelWidth(initialFixedSideSize);
  }, [initialFixedSideSize]);

  const fullScreen = hideSide === SplitAlignment.FIRST || hideSide === SplitAlignment.LAST;

  useBrowserLayoutEffect(() => {
    if (!leftRef.current || !rightRef.current || !separatorRef.current) {
      return;
    }

    if (hideSide === SplitAlignment.FIRST) {
      leftRef.current.style.right = "100%";
      rightRef.current.style.left = "0px";
      separatorRef.current.style.display = "none";
    } else if (hideSide === SplitAlignment.LAST) {
      leftRef.current.style.right = "0px";
      rightRef.current.style.left = "100%";
      separatorRef.current.style.display = "none";
    } else {
      separatorRef.current.style.display = "block";
      separatorRef.current.style.left = "";
      leftRef.current.style.left = "";
      leftRef.current.style.right = "";
      rightRef.current.style.left = "";
      rightRef.current.style.right = "";
    }
  }, [hideSide]);

  useBrowserLayoutEffect(() => {
    let mouseInfo: HorizontalSplitMouseInfo;

    // wrap this field in an object so we don't end up with weird
    // closure issues.
    const effectState = { isResizing: false };
    if (!separatorRef.current || !leftRef.current || !rightRef.current || fullScreen) {
      return undefined;
    }

    const onMouseMove = (e: MouseEvent) => {
      const delta = {
        x: e.clientX - mouseInfo.event.clientX,
        y: e.clientY - mouseInfo.event.clientY,
      };
      effectState.isResizing = true;

      if (!splitGroupRef.current || !separatorRef.current || !leftRef.current || !rightRef.current) {
        return;
      }

      const w = splitGroupRef.current.clientWidth;
      const minSize = minPanelSize ?? 0;
      let splitX = mouseInfo.offsetLeft + delta.x;

      if (splitX < minSize) {
        splitX = minSize;
      }

      if (splitX > w - minSize) {
        splitX = w - minSize;
      }

      separatorRef.current.style.left = splitX - separatorRef.current.offsetWidth / 2 + "px";
      leftRef.current.style.right = w - splitX + "px";
      rightRef.current.style.left = splitX + "px";
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.preventDefault) {
        e.preventDefault();
      }

      if (!splitGroupRef.current || !separatorRef.current || !leftRef.current || !rightRef.current) {
        return;
      }

      const w = splitGroupRef.current.clientWidth - separatorRef.current.offsetWidth;
      mouseInfo = {
        event: e,
        offsetLeft: separatorRef.current.offsetLeft,
        offsetTop: separatorRef.current.offsetTop,
        leftWidth: leftRef.current.offsetWidth,
        rightWidth: w - leftRef.current.offsetWidth,
      };

      effectState.isResizing = true;

      separatorRef.current.style.transition = "0s";
      document.onmousemove = onMouseMove;
      document.onmouseup = () => {
        effectState.isResizing = false;

        if (!splitGroupRef.current || !separatorRef.current || !leftRef.current || !rightRef.current) {
          return;
        }

        separatorRef.current.style.transition = "0.25s";
        if (fixedSide === SplitAlignment.FIRST) {
          setFixedPanelWidth(leftRef.current.offsetWidth);
        } else if (fixedSide === SplitAlignment.LAST) {
          setFixedPanelWidth(rightRef.current.offsetWidth);
        }
        document.onmousemove = document.onmouseup = null;
      };
    };

    const onResize = () => {
      if (!splitGroupRef.current || !separatorRef.current || !leftRef.current || !rightRef.current) {
        return;
      }

      const sepWidth = separatorRef.current.offsetWidth;

      if (!separatorRef.current.parentElement || fixedPanelWidth === undefined) {
        return;
      }

      const w = separatorRef.current.parentElement.clientWidth;
      if (flexibleSide === SplitAlignment.FIRST) {
        const rightWidth = fixedPanelWidth;
        separatorRef.current.style.left = w - rightWidth - sepWidth / 2 + "px";
        leftRef.current.style.right = rightWidth + "px";
        rightRef.current.style.left = w - rightWidth + "px";
      } else if (flexibleSide === SplitAlignment.LAST) {
        const leftWidth = fixedPanelWidth;
        separatorRef.current.style.left = leftWidth - sepWidth / 2 + "px";
        rightRef.current.style.left = leftWidth + "px";
        leftRef.current.style.right = w - leftWidth + "px";
      } else {
        rightRef.current.style.left = leftRef.current.offsetWidth + "px";
        separatorRef.current.style.left = leftRef.current.offsetHeight - sepWidth / 2 + "px";
      }
    };

    onResize();
    if (draggable) {
      separatorRef.current.addEventListener("mousedown", onMouseDown);
    }

    return () => {
      if (!fullScreen && draggable && separatorRef.current) {
        separatorRef.current.removeEventListener("mousedown", onMouseDown);
      }
    };
  }, [splitGroupRect, draggable, fullScreen]);

  // Handle separator highlighting on hover
  useBrowserLayoutEffect(() => {
    if (!separatorRef.current || !highlightSeparator || fullScreen) {
      return undefined;
    }

    const separator = separatorRef.current;
    let hoverTimeout: NodeJS.Timeout | undefined;

    // Use a timeout delay to avoid unnecessary highlights on hover
    const onMouseEnter = () => {
      hoverTimeout = setTimeout(() => {
        separator.classList.add(styles.highlightSeparator);
      }, 200);
    };

    const onMouseLeave = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = undefined;
      }
      separator.classList.remove(styles.highlightSeparator);
    };

    separator.addEventListener("mouseenter", onMouseEnter);
    separator.addEventListener("mouseleave", onMouseLeave);

    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      separator.removeEventListener("mouseenter", onMouseEnter);
      separator.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [highlightSeparator, fullScreen]);

  const leftStyle: CSSProperties = { ...leftStyleOverrides };
  const rightStyle: CSSProperties = { ...rightStyleOverrides };

  if (splitGroupRef.current) {
    leftStyle.opacity = 1.0;
    rightStyle.opacity = 1.0;
  }

  return (
    <div className={cx(styles.horizontalSplitGroup, containerClassNames)} ref={splitGroupRef}>
      <div className={styles.left} style={leftStyle} ref={leftRef}>
        <div className={cx(styles.panel, "Split-leftPanel", leftProps?.scrollControlled && styles.scrollControlled)}>
          {firstChild}
        </div>
      </div>
      <div
        className={cx(styles.horizontalSeparator, (!draggable || fullScreen) && styles.disabled)}
        ref={separatorRef}
      />
      <div className={styles.right} style={rightStyle} ref={rightRef}>
        <div className={cx(styles.panel, "Split-rightPanel", rightProps?.scrollControlled && styles.scrollControlled)}>
          {lastChild}
        </div>
      </div>
    </div>
  );
}

interface VerticalMouseInfo {
  event: MouseEvent;
  offsetLeft: number;
  offsetTop: number;
  splitY: number;
}

export function VerticalSplitGroup({
  bottomStyleOverrides = {},
  flexibleSide,
  initialFixedSideSize,
  topStyleOverrides = {},
  containerClassNames,
  draggable = true,
  hideSide = SplitAlignment.NONE,
  minPanelSize,
  children: [firstChild, lastChild],
  highlightSeparator = false,
}: VerticalSplitProps) {
  let fixedSide: SplitAlignment;
  if (flexibleSide === SplitAlignment.LAST) {
    fixedSide = SplitAlignment.FIRST;
  } else if (flexibleSide === SplitAlignment.FIRST) {
    fixedSide = SplitAlignment.LAST;
  } else {
    fixedSide = SplitAlignment.NONE;
  }

  const splitGroupRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const separatorRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [fixedPanelHeight, setFixedPanelHeight] = useState<number | undefined>(initialFixedSideSize);
  const splitGroupRect = useResizeObserver(splitGroupRef);

  //allow for dynamic updating of panel heights.
  useEffect(() => {
    setFixedPanelHeight(initialFixedSideSize);
  }, [initialFixedSideSize]);

  const fullScreen = hideSide === SplitAlignment.FIRST || hideSide === SplitAlignment.LAST;

  useBrowserLayoutEffect(() => {
    if (!splitGroupRef.current || !separatorRef.current || !topRef.current || !bottomRef.current) {
      return;
    }

    if (hideSide === SplitAlignment.FIRST) {
      topRef.current.style.bottom = "100%";
      bottomRef.current.style.top = "0px";
      separatorRef.current.style.display = "none";
    } else if (hideSide === SplitAlignment.LAST) {
      topRef.current.style.bottom = "0px";
      bottomRef.current.style.top = "100%";
      separatorRef.current.style.display = "none";
    } else {
      separatorRef.current.style.display = "block";
      separatorRef.current.style.top = "";
      topRef.current.style.top = "";
      topRef.current.style.bottom = "";
      bottomRef.current.style.top = "";
      bottomRef.current.style.bottom = "";
    }
  }, [hideSide]);

  useBrowserLayoutEffect(() => {
    let mouseInfo: VerticalMouseInfo;

    // wrap this field in an object so we don't end up with weird
    // closure issues.
    const effectState = {
      isResizing: false,
    };

    if (!separatorRef.current || !topRef.current || !bottomRef.current || fullScreen) {
      return undefined;
    }

    const onMouseMove = (e: MouseEvent) => {
      const delta = {
        x: e.clientX - mouseInfo.event.clientX,
        y: e.clientY - mouseInfo.event.clientY,
      };

      if (!splitGroupRef.current || !separatorRef.current || !topRef.current || !bottomRef.current) {
        return;
      }

      const h = splitGroupRef.current.clientHeight;
      const sepHeight = separatorRef.current.offsetHeight;

      effectState.isResizing = true;

      let splitY = mouseInfo.offsetTop + delta.y;

      minPanelSize = minPanelSize ?? 0;

      // make sure we're not violating minHeight
      if (splitY < minPanelSize) {
        splitY = minPanelSize;
      }

      if (splitY > h - minPanelSize) {
        splitY = h - minPanelSize;
      }

      mouseInfo.splitY = splitY;

      separatorRef.current.style.top = splitY - sepHeight / 2 + "px";
      topRef.current.style.bottom = h - splitY + "px";
      bottomRef.current.style.top = splitY + "px";
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.preventDefault) {
        e.preventDefault();
      }

      if (!splitGroupRef.current || !separatorRef.current || !topRef.current || !bottomRef.current) {
        return;
      }

      mouseInfo = {
        event: e,
        offsetLeft: separatorRef.current.offsetLeft,
        offsetTop: separatorRef.current.offsetTop,
        splitY: e.clientY,
      };

      effectState.isResizing = true;

      separatorRef.current.style.transition = "0s";
      document.onmousemove = onMouseMove;
      document.onmouseup = () => {
        if (!splitGroupRef.current || !separatorRef.current) {
          return;
        }

        separatorRef.current.style.transition = "0.25s";
        effectState.isResizing = false;
        const { splitY } = mouseInfo;
        if (fixedSide === SplitAlignment.FIRST) {
          setFixedPanelHeight(splitY);
        } else if (fixedSide === SplitAlignment.LAST) {
          const h = splitGroupRef.current.clientHeight - splitY;
          setFixedPanelHeight(h);
        }

        document.onmousemove = document.onmouseup = null;
      };
    };

    const onResize = () => {
      if (effectState.isResizing) {
        return;
      }

      if (!splitGroupRef.current || !separatorRef.current || !topRef.current || !bottomRef.current) {
        return;
      }

      const sepHeight = separatorRef.current.offsetHeight;

      if (!separatorRef.current.parentElement || fixedPanelHeight === undefined) {
        return;
      }

      const h = separatorRef.current.parentElement.clientHeight;

      if (flexibleSide === SplitAlignment.FIRST) {
        const bottomHeight = fixedPanelHeight;
        bottomRef.current.style.top = h - bottomHeight + "px";
        topRef.current.style.bottom = bottomHeight + "px";
        separatorRef.current.style.top = h - bottomHeight - sepHeight / 2 + "px";
      } else if (flexibleSide === SplitAlignment.LAST) {
        const topHeight = fixedPanelHeight;
        topRef.current.style.bottom = h - topHeight + "px";
        bottomRef.current.style.top = topHeight + "px";
        separatorRef.current.style.top = topHeight - sepHeight / 2 + "px";
      } else {
        bottomRef.current.style.top = topRef.current.offsetHeight + "px";
        separatorRef.current.style.top = topRef.current.offsetHeight - sepHeight / 2 + "px";
      }
    };

    onResize();
    if (draggable) {
      separatorRef.current.addEventListener("mousedown", onMouseDown);
    }

    return () => {
      if (draggable && !fullScreen && separatorRef.current) {
        separatorRef.current.removeEventListener("mousedown", onMouseDown);
      }
    };
  }, [splitGroupRect, draggable, fullScreen]);

  // Handle separator highlighting on hover
  useBrowserLayoutEffect(() => {
    if (!separatorRef.current || !highlightSeparator || fullScreen) {
      return undefined;
    }

    const separator = separatorRef.current;
    let hoverTimeout: NodeJS.Timeout | undefined;

    const onMouseEnter = () => {
      hoverTimeout = setTimeout(() => {
        separator.classList.add(styles.highlightSeparator);
      }, 200);
    };

    const onMouseLeave = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = undefined;
      }
      separator.classList.remove(styles.highlightSeparator);
    };

    separator.addEventListener("mouseenter", onMouseEnter);
    separator.addEventListener("mouseleave", onMouseLeave);

    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      separator.removeEventListener("mouseenter", onMouseEnter);
      separator.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [highlightSeparator, fullScreen]);

  const topStyle = { ...topStyleOverrides };
  const bottomStyle = { ...bottomStyleOverrides };

  if (separatorRef.current) {
    topStyle.opacity = 1.0;
    bottomStyle.opacity = 1.0;
  }

  return (
    <div className={cx(styles.verticalSplitGroup, containerClassNames)} ref={splitGroupRef}>
      <div className={styles.top} ref={topRef} style={topStyle}>
        <div className={styles.panel}>{firstChild}</div>
      </div>
      <div ref={separatorRef} className={cx(styles.verticalSeparator, (!draggable || fullScreen) && styles.disabled)} />
      <div className={styles.bottom} ref={bottomRef} style={bottomStyle}>
        <div className={styles.panel}>{lastChild}</div>
      </div>
    </div>
  );
}

export type SplitGroupDirection = "vertical" | "horizontal";
type SplitGroupProps = { direction: SplitGroupDirection; flip?: boolean } & HorizontalSplitProps & VerticalSplitProps;

// To prevent re-mounting children, the above components should be combined into one
export function SplitGroup({
  children: [firstChild, lastChild],
  hideSide,
  direction,
  flip,
  ...props
}: SplitGroupProps) {
  const Split = direction === "vertical" ? VerticalSplitGroup : HorizontalSplitGroup;

  if (flip) {
    if (hideSide === SplitAlignment.FIRST) {
      hideSide = SplitAlignment.LAST;
    } else if (hideSide === SplitAlignment.LAST) {
      hideSide = SplitAlignment.FIRST;
    }
  }
  return (
    <Split {...props} hideSide={hideSide}>
      {!flip ? [firstChild, lastChild] : [lastChild, firstChild]}
    </Split>
  );
}
